// Internal modules
import { appStore } from '@/store';
import { apolloMutation } from '@/lib/apollo';
import { features, getToken, logger, redirectToGenericError } from '@/lib';
import {
  UPDATE_CALLBACK_OPTIONS_MUTATION,
  UPDATE_PROFILE_SHIPPING_OPTIONS_MUTATION,
} from '../mutations';
import { mapCallbackShippingMethodsInput } from '../lenses/mapCallbackShippingMethodsInput';
import { mapProfileShippingMethodsInput } from '../lenses/mapProfileShippingMethodsInput';
import { logShippingOptionSelectedToFPTI } from '../../fpti';
import { PROFILE_SHIPPING_OPTIONS_QUERY } from '../queries/profileShippingOptions.gql';

/**
 * @function updateShippingOptions
 * @param {boolean} isCallBackShippingOption
 * @description calls callback or profile shipping option mutation
 */
const updateShippingOptions = () => {
  const { form: formState, api: apiState } = appStore.getState();
  const { shippingInsuranceSelected } = formState;
  const { profileOverrideAllowed, shippingMethods, shippingOptionsSource } =
    apiState;
  const hideShipping = features.isActive('hideShipping');

  if (!shippingMethods.length) {
    return;
  }

  let mutation;
  let variables;
  const isCallBackShippingOption = shippingOptionsSource !== 'PROFILE';

  if (isCallBackShippingOption) {
    mutation = UPDATE_CALLBACK_OPTIONS_MUTATION;
    variables = {
      callbackShippingMethods: mapCallbackShippingMethodsInput(shippingMethods),
      shippingOptionsSource,
      token: getToken(),
    };
  } else {
    mutation = UPDATE_PROFILE_SHIPPING_OPTIONS_MUTATION;
    /*
      By passing the hideShipping and profileOverrideAllowed flags,
      xobuyernodeserv can then determine the correct shipping cost
      https://github.paypal.com/PayPal-R/xobuyernodeserv/blob/7de2d19ec5f5dd3f92c31cbc3c462e982c0d3a73/src/graphql/data/Checkout/shipping/lenses.js#L240-L258
    */
    variables = {
      hideShipping,
      profileOverrideAllowed,
      profileShippingMethods: mapProfileShippingMethodsInput(shippingMethods),
      token: getToken(),
    };
  }

  const selectedMethod = shippingMethods.find(method => method.selected);
  logShippingOptionSelectedToFPTI(
    shippingOptionsSource,
    selectedMethod.label,
    shippingInsuranceSelected,
  );

  return apolloMutation({
    mutation: mutation,
    // We cache the response for this call for use in Apple Pay on BASL
    update: (cache, { data }) => {
      const updateProfileShippingOptionsData =
        data?.updateProfileShippingOptions || {};
      if (updateProfileShippingOptionsData) {
        cache.writeQuery({
          data: { checkoutSession: updateProfileShippingOptionsData },
          query: PROFILE_SHIPPING_OPTIONS_QUERY,
        });
      }
    },
    variables,
  })
    .then(({ data }) => {
      const { api: apiState } = appStore.getState();
      const { cartDetails } = apiState;
      const { api: apiActions } = appStore.getActions();
      const { setCartData } = apiActions;
      const { amounts } = isCallBackShippingOption
        ? data.updateCallbackOptions.cart
        : data.updateProfileShippingOptions.cart;

      setCartData({
        cartAmount: amounts.total,
        cartDetails: {
          // Need to spread old values or else everything else in cartDetails
          // would be overwritten
          ...cartDetails,
          amounts,
        },
      });
      return data;
    })
    .catch(error => {
      logger.error('snt_patch_cart_error', { error });
      redirectToGenericError();
    });
};

export { updateShippingOptions };
